import React, { Suspense } from 'react';
import { Streamdown } from 'streamdown';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

const VegaChart = React.lazy(() => import('./VegaChart'));

const StyledPre = styled('pre')(({ theme }) => ({
	backgroundColor: theme.palette.grey[100],
	border: `1px solid ${theme.palette.grey[300]}`,
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(2),
	whiteSpace: 'pre-wrap',
	wordBreak: 'break-all',
	overflowX: 'auto',
	fontSize: '0.9rem',
	fontFamily: 'monospace',
}));

const defaultContent = `
Here is a simple bar chart:

\`\`\`vega-lite
{
  "$schema": "https://vega.github.io/schema/vega-lite/v5.json",
  "description": "A simple bar chart with embedded data.",
  "data": {
    "values": [
      {"a": "A", "b": 28}, {"a": "B", "b": 55}, {"a": "C", "b": 43},
      {"a": "D", "b": 91}, {"a": "E", "b": 81}, {"a": "F", "b": 53},
      {"a": "G", "b": 19}, {"a": "H", "b": 87}, {"a": "I", "b": 52}
    ]
  },
  "mark": "bar",
  "encoding": {
    "x": {"field": "a", "type": "ordinal"},
    "y": {"field": "b", "type": "quantitative"}
  }
}
\`\`\`

And here is some more text following the chart.
`;

/**
 * Enhanced Markdown Renderer that handles both regular markdown and Vega-Lite charts
 * using Streamdown's custom component rendering.
 */
const EnhancedMarkdownRenderer = ({ content = defaultContent }) => {

	const components = {
		code: ({ className, children }) => {
			const language = className?.replace('language-', '');

			if (language === 'vega-lite') {
				try {
					// Clean the JSON string by removing markdown link syntax
					const cleanedJson = children.replace(/\[.*?\]\((.*?)\)/g, '$1');
					const spec = JSON.parse(cleanedJson);
					return (
						<Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', marginY: 2 }}><CircularProgress /></Box>}>
							<Box sx={{ marginY: 2, width: '100%', minWidth: '50vw', maxWidth: '80vw' }}>
								<VegaChart spec={spec} />
							</Box>
						</Suspense>
					);
				} catch (error) {
					return (
						<Box 
							sx={{
								color: '#d32f2f', padding: 2, border: '1px solid #d32f2f',
								borderRadius: 1, backgroundColor: '#ffebee', fontFamily: 'monospace', marginY: 2
							}}
						>
							<strong>Error parsing chart:</strong> {error.message}
							<details style={{ marginTop: '0.5rem' }}>
								<summary style={{ cursor: 'pointer', color: '#1976d2' }}>Show raw spec</summary>
								<pre style={{ marginTop: '0.5rem', fontSize: '0.875rem', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
									{children}
								</pre>
							</details>
						</Box>
					);
				}
			}

			// Render standard code blocks
			return (
				<StyledPre>
					<code>{children}</code>
				</StyledPre>
			);
		},
		// Ensure pre tags are not rendered by default, as we handle it in the code component
		pre: ({ children }) => <>{children}</>
	};

	return (
		<Box 
			sx={{ 
				width: '100%',
				'& .vega-embed summary': { display: 'none' }
			}}
		>
			<Streamdown components={components}>
				{content}
			</Streamdown>
		</Box>
	);
};

export default EnhancedMarkdownRenderer;
